/**
 * Pool content URL query parameters.
 *
 * Query keys that affect which content Pool fetches use the `kz_` prefix
 * (Kueez shell params), so they do not collide with host or ad tracking params.
 */

/** Kueez shell query-param keys (`kz_` prefix so they don't collide with host/ad params). */
export enum QueryParam {
  /** Kueez CMS post id. */
  PostId = 'kz_pid',
  /** Pool content locale. */
  Locale = 'kz_lo',
  /** SDK verbose logging. */
  Debug = 'kz_debug',
  /** Pool draft/private-mode fetches (`include_drafts=1`). */
  IncludeDrafts = 'kz_prv',
  /** Pool/website domain override. */
  Domain = 'kz_domain',
  /** Local country override (ISO 3166-1 alpha-2 → pool `?cc=`). */
  Country = 'kz_cc',
  /** Analytics activity id override (`ac=` on /dye). */
  ActivityId = 'kz_ac',
  /** Analytics activity model override (`acm=` on /dye). */
  ActivityModel = 'kz_acm',
  /** Activate the mbp module. */
  Mbp = 'kz_mbp',
}

/** Reads a trimmed string query param from the page URL (empty string when absent). */
export function resolveStringFromUrl(key: string, search?: string): string {
  const raw = search ?? (typeof window !== 'undefined' ? window.location.search : '');
  return (new URLSearchParams(raw).get(key) ?? '').trim();
}

function looksLikeSearch(value: string): boolean {
  return value.startsWith('?') || value.includes('=');
}

function parseQueryBool(value: string | null): boolean | undefined {
  if (value === null) return undefined;
  const trimmed = value.trim().toLowerCase();
  if (!trimmed || trimmed === '1' || trimmed === 'true' || trimmed === 'yes') return true;
  if (trimmed === '0' || trimmed === 'false' || trimmed === 'no') return false;
  return undefined;
}

export function resolveBoolFromUrl(key: string, fallback?: boolean): boolean | undefined;
export function resolveBoolFromUrl(key: string, search: string | undefined, fallback?: boolean): boolean | undefined;
export function resolveBoolFromUrl(
  key: string,
  searchOrFallback?: string | boolean,
  fallbackArg?: boolean,
): boolean | undefined {
  const hasSearchArg = typeof searchOrFallback === 'string' && looksLikeSearch(searchOrFallback);
  const raw =
    (hasSearchArg ? searchOrFallback : undefined) ??
    (typeof window !== 'undefined' ? window.location.search : '');
  const fallback = hasSearchArg
    ? fallbackArg
    : (typeof searchOrFallback === 'boolean' ? searchOrFallback : fallbackArg);
  const parsed = parseQueryBool(new URLSearchParams(raw).get(key));
  if (parsed !== undefined) return parsed;
  return fallback;
}

export function resolveDebugFromUrl(fallback?: boolean): boolean | undefined;
export function resolveDebugFromUrl(search: string | undefined, fallback?: boolean): boolean | undefined;
export function resolveDebugFromUrl(
  searchOrFallback?: string | boolean,
  fallbackArg?: boolean,
): boolean | undefined {
  if (typeof searchOrFallback === 'string') {
    return resolveBoolFromUrl(QueryParam.Debug, searchOrFallback, fallbackArg);
  }
  return resolveBoolFromUrl(QueryParam.Debug, searchOrFallback);
}

export function resolveIncludeDraftsFromUrl(fallback?: boolean): boolean | undefined;
export function resolveIncludeDraftsFromUrl(search: string | undefined, fallback?: boolean): boolean | undefined;
export function resolveIncludeDraftsFromUrl(
  searchOrFallback?: string | boolean,
  fallbackArg?: boolean,
): boolean | undefined {
  if (typeof searchOrFallback === 'string') {
    return resolveBoolFromUrl(QueryParam.IncludeDrafts, searchOrFallback, fallbackArg);
  }
  return resolveBoolFromUrl(QueryParam.IncludeDrafts, searchOrFallback);
}

export function resolveDomainFromUrl(fallback?: string): string;
export function resolveDomainFromUrl(search: string | undefined, fallback?: string): string;
export function resolveDomainFromUrl(
  searchOrFallback?: string,
  fallbackArg?: string,
): string {
  const hasSearchArg = typeof searchOrFallback === 'string' && looksLikeSearch(searchOrFallback);
  const raw =
    (hasSearchArg ? searchOrFallback : undefined) ??
    (typeof window !== 'undefined' ? window.location.search : '');
  const fallback = hasSearchArg ? fallbackArg : (searchOrFallback ?? fallbackArg);
  const fromUrl = new URLSearchParams(raw).get(QueryParam.Domain);
  const trimmed = (fromUrl ?? '').trim();
  if (trimmed) return trimmed;
  if (fallback !== undefined) {
    const fb = String(fallback).trim();
    if (fb) return fb;
  }
  return '';
}

export function resolvePostIdFromUrl(fallback?: string | number): string;
export function resolvePostIdFromUrl(search: string | undefined, fallback?: string | number): string;
export function resolvePostIdFromUrl(
  searchOrFallback?: string | number,
  fallbackArg?: string | number,
): string {
  const hasSearchArg = typeof searchOrFallback === 'string' && looksLikeSearch(searchOrFallback);
  const raw =
    (hasSearchArg ? searchOrFallback : undefined) ??
    (typeof window !== 'undefined' ? window.location.search : '');
  const fallback = hasSearchArg ? fallbackArg : (searchOrFallback ?? fallbackArg);
  const fromUrl = new URLSearchParams(raw).get(QueryParam.PostId);
  const trimmed = (fromUrl ?? '').trim();
  if (trimmed) return trimmed;
  if (fallback !== undefined) {
    const fb = String(fallback).trim();
    if (fb) return fb;
  }
  return '';
}

export function resolveLocaleFromUrl(fallback?: string): string;
export function resolveLocaleFromUrl(search: string | undefined, fallback?: string): string;
export function resolveLocaleFromUrl(
  searchOrFallback?: string,
  fallbackArg?: string,
): string {
  const hasSearchArg = typeof searchOrFallback === 'string' && looksLikeSearch(searchOrFallback);
  const raw =
    (hasSearchArg ? searchOrFallback : undefined) ??
    (typeof window !== 'undefined' ? window.location.search : '');
  const fallback = hasSearchArg ? fallbackArg : (searchOrFallback ?? fallbackArg);
  const fromUrl = new URLSearchParams(raw).get(QueryParam.Locale);
  const trimmed = (fromUrl ?? '').trim().toLowerCase();
  if (trimmed) return trimmed;
  if (fallback !== undefined) {
    const fb = String(fallback).trim().toLowerCase();
    if (fb) return fb;
  }
  return '';
}

export function resolveCountryFromUrl(fallback?: string): string;
export function resolveCountryFromUrl(search: string | undefined, fallback?: string): string;
export function resolveCountryFromUrl(
  searchOrFallback?: string,
  fallbackArg?: string,
): string {
  const hasSearchArg = typeof searchOrFallback === 'string' && looksLikeSearch(searchOrFallback);
  const raw =
    (hasSearchArg ? searchOrFallback : undefined) ??
    (typeof window !== 'undefined' ? window.location.search : '');
  const fallback = hasSearchArg ? fallbackArg : (searchOrFallback ?? fallbackArg);
  const fromUrl = new URLSearchParams(raw).get(QueryParam.Country);
  const trimmed = (fromUrl ?? '').trim().toUpperCase();
  if (/^[A-Z]{2}$/.test(trimmed)) return trimmed;
  if (fallback !== undefined) {
    const fb = String(fallback).trim().toUpperCase();
    if (/^[A-Z]{2}$/.test(fb)) return fb;
  }
  return '';
}
